import { Button, Form, Input, Select, Space, Switch } from 'antd'
import React, { useEffect } from 'react'

import S3Upload from '@a4b/ui/src/modules/monetisation/puja/molecules/S3Upload'
import { PUJA_PRESIGN_URL, YODA_CDN_URL } from '@/utils/constants'
import {
    Temple,
    TemplePayload,
} from '@a4b/api/src/modules/Monetisation/Puja/types/templeTypes'
import { STATES_EN } from '@/modules/monetisation/puja/ManageStores/CreateStore/utils/constants'

const { Option } = Select

interface TempleFormProps {
    initialValues?: Temple
    onFinish: (values: TemplePayload) => void
    loading?: boolean
}

const TempleForm: React.FC<TempleFormProps> = ({
    initialValues,
    onFinish,
    loading,
}) => {
    const [form] = Form.useForm()

    useEffect(() => {
        if (initialValues) {
            // S3Upload expects array of URL strings
            const formValues = {
                ...initialValues,
                image:
                    initialValues.image && initialValues.image.trim() !== ''
                        ? [initialValues.image]
                        : [],
            }
            form.setFieldsValue(formValues)
        } else {
            form.resetFields()
            form.setFieldsValue({ active: false })
        }
    }, [initialValues, form])

    const handleFinish = (values: any) => {
        // Extract URL from S3Upload fileList format
        // S3Upload returns array, we need to get the URL string
        let imageUrl = ''
        if (values.image && values.image.length > 0) {
            const imageItem = values.image[0]
            // Handle both formats: string or object with url/response
            if (typeof imageItem === 'string') {
                imageUrl = imageItem
            } else if (imageItem.url) {
                imageUrl = imageItem.url
            } else if (imageItem.response?.url) {
                imageUrl = imageItem.response.url
            }
        }

        onFinish({
            ...values,
            image: imageUrl,
            active: !!values.active,
        })
    }

    return (
        <Form
            form={form}
            layout='vertical'
            onFinish={handleFinish}
            initialValues={{ active: false }}
        >
            <Form.Item
                name='title_en'
                label='Title (English)'
                rules={[{ required: true, message: 'Please enter title in English' }]}
            >
                <Input placeholder='Enter temple name' />
            </Form.Item>

            <Form.Item
                name='title_hi'
                label='Title (Hindi)'
                rules={[{ required: true, message: 'Please enter title in Hindi' }]}
            >
                <Input placeholder='Enter temple name in Hindi' />
            </Form.Item>

            <Form.Item
                name='description'
                label='Description'
                rules={[{ required: true, message: 'Please enter description' }]}
            >
                <Input.TextArea rows={4} placeholder='Enter temple description' />
            </Form.Item>

            <Form.Item
                name='image'
                label='Temple Image'
                rules={[
                    {
                        required: true,
                        validator: async (_, values) => {
                            if (!values || values.length === 0) {
                                return Promise.reject('Please upload Temple Image')
                            }
                            return Promise.resolve()
                        },
                    },
                ]}
                valuePropName='fileList'
            >
                <S3Upload
                    cdnUrl={YODA_CDN_URL}
                    preSignUrl={PUJA_PRESIGN_URL}
                    min={1}
                    max={1}
                />
            </Form.Item>

            <Form.Item
                name='city'
                label='City'
                rules={[{ required: true, message: 'Please enter city' }]}
            >
                <Input placeholder='Enter city' />
            </Form.Item>

            <Form.Item
                name='state'
                label='State'
                rules={[{ required: true, message: 'Please select state' }]}
            >
                <Select placeholder='Select state' showSearch>
                    {STATES_EN.map(state => (
                        <Option key={state.code} value={state.name}>
                            {state.name}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item name='active' label='Active' valuePropName='checked'>
                <Switch />
            </Form.Item>

            <Form.Item>
                <Space>
                    <Button type='primary' htmlType='submit' loading={loading}>
                        {initialValues ? 'Update' : 'Create'}
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    )
}

export default TempleForm
